import React, { useEffect, useRef, useCallback } from 'react'
import {
    getMessages,
    getScrollState,
    retrieveMessageHistory,
    syncMessages,
    browseMessageHistory,
} from 'redux/slices/chat'
import { useDispatch, useSelector } from 'react-redux'

import ChatMessage from 'components/set/ChatMessage/ChatMessage'
import NewMessageButton from 'components/set/NewMessageButton/NewMessageButton'
import { useStyles } from './styles'
import clsx from 'clsx'

const ChatMessageList: React.FC<{ fullScreen: boolean }> = ({ fullScreen = false }) => {
    const dispatch = useDispatch()

    const messageList = useRef<HTMLDivElement>(null)
    const forcedScroll = useRef(false)
    const prevScroll = useRef(0)

    const classes = useStyles()
    const messages = useSelector(getMessages)
    const scrollState = useSelector(getScrollState)

    const scrollToBottom = useCallback(() => {
        if (!messageList.current) return

        forcedScroll.current = true
        messageList.current.scrollTop = messageList.current.scrollHeight
        if (scrollState === 'history') {
            dispatch(syncMessages())
        }
    }, [dispatch, scrollState])

    const handleScroll = useCallback(() => {
        if (!messageList.current) return
        if (forcedScroll.current) return (forcedScroll.current = false)

        const { scrollTop, scrollHeight, clientHeight } = messageList.current

        if (scrollTop === 0) {
            dispatch(browseMessageHistory())
            dispatch(retrieveMessageHistory())
        } else if (scrollHeight - scrollTop === clientHeight) {
            dispatch(syncMessages())
        } else if (scrollState === 'sync' && scrollTop < prevScroll.current) {
            dispatch(browseMessageHistory())
        }

        prevScroll.current = scrollTop
    }, [dispatch, scrollState])

    useEffect(() => {
        if (scrollState === 'sync') scrollToBottom()
    }, [messages, scrollToBottom, scrollState])

    return (
        <>
            <div
                className={clsx(classes.messages, {
                    [classes.fullScreen]: fullScreen,
                })}
                ref={messageList}
                onScroll={handleScroll}
            >
                {messages.map(msg => (
                    <ChatMessage msg={msg} key={msg.msgId} />
                ))}
            </div>
            <div className={classes.newMessageButton}>
                {scrollState === 'history' && <NewMessageButton onClick={scrollToBottom} />}
            </div>
        </>
    )
}

export default React.memo(ChatMessageList)
